import React from 'react';
import {
  ControlledDatePicker,
  ControlledInput,
  ControlledRadioButton,
  ControlledSelect
} from '@@viewCommonComponents/controlled/index.js';
import booleanOptions from '@@viewCommonComponents/forms/options/index.js';
import { isNilOrEmpty, isNotNilOrEmpty } from '@@serverHelpers/index.mjs';
import {
  formatZipCode,
  isEmailValid,
  isRequired,
  isRequiredBoolean,
  isRequiredSelect,
  isValidContractNumber,
  zipCodeRules
} from '@@viewHelpers/validations/index.js';
import moment from 'moment';
import useActivePolicyForm from './hooks/useActivePolicyForm.js';
import { Icon, InfoMessage, PageTitle } from '@fasstech/middle-design-system';
import { FormProvider, useForm } from 'react-hook-form';
import { PageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import {
  assocPath,
  compose,
  equals,
  filter,
  identity,
  length,
  lt,
  map,
  modify,
  modifyPath,
  not,
  path,
  pathOr,
  prop,
  propOr,
  when
} from 'ramda';
import { dateTsToFr } from '@@viewHelpers/date.js';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer.jsx';
import extraCurrentCoverageDisabled from './extra/extraCurrentCoverageDisabled.js';
import { extraHandleSaveButtonRia } from './extra/extraHandleSaveButtonRia.js';
import { useAnalytics } from '@@app/context/analytics-context.js';
import { QGlobalDataQuery } from '@@app/graphql/index.js';
import { useQuery } from '@apollo/client';

const SouscriptionActivePolicyForm = ({
  getMiddleText,
  middleFeatures,
  project
}) => {
  const offerCode = prop('offerCode', project);
  const isGroupHealth = offerCode === 'GROUP_HEALTH';
  const activePoliciesPath = isGroupHealth
    ? ['company', 'activePolicies']
    : ['customer', 'activePolicies'];
  const basePath = isGroupHealth
    ? 'company.activePolicies.0'
    : 'customer.activePolicies.0';

  const formatActivePoliciesDate = when(
    () => isNotNilOrEmpty(path(activePoliciesPath, project)),
    modifyPath(
      activePoliciesPath,
      map(modify('requestedCancellationDate', when(isNotNilOrEmpty, dateTsToFr)))
    )
  );
  const { trackEvent } = useAnalytics();

  const { data } = useQuery(QGlobalDataQuery, {
    fetchPolicy: 'cache-and-network'
  });

  const getAnalyticsInformation = pathOr({}, ['getAnalyticsInformation', 'data'], data);

  const { IN_CHARGE_RESP = [] } = middleFeatures;
  const authorizedInChargeResp = filter(compose(equals('true'), propOr(false, 'AUTORISE')))(
    IN_CHARGE_RESP || []
  );
  const displayInChargeResp = compose(lt(1), length)(authorizedInChargeResp);

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: compose(
      assocPath(
        ['activePolicy', 'contractOnGoing'],
        isNotNilOrEmpty(pathOr(false, activePoliciesPath, project))
      ),
      displayInChargeResp
        ? identity
        : compose(
          assocPath(
            [...activePoliciesPath, 0, 'cancellationApplicant'],
            path([0, 'CODE'], authorizedInChargeResp)
          ),
          when(
            () => isNilOrEmpty(path(activePoliciesPath, project)),
            assocPath(activePoliciesPath, [])
          )
        )
    )(formatActivePoliciesDate(project))
  });

  const {
    contractOnGoing,
    inChargeOptions,
    selectedInChargeData,
    onSubmit,
    onPrevious,
    fieldsAreRequired,
    isValid,
    jrmcCompanies,
    jrmc,
    effectDate
  } = useActivePolicyForm({
    middleFeatures,
    form,
    project,
    activePoliciesPath,
    basePath
  });

  const minDate = moment(Number(effectDate)).subtract(1, 'day').toDate();
  const maxDate = moment(Number(effectDate)).add(1, 'years').toDate();

  return (
    <FormProvider {...form}>
      <form className={'w-full'} onSubmit={form.handleSubmit(onSubmit)}>
        <PageLayout>
          <Icon name={'healthCheckup'} size={70}/>
          <PageTitle>{getMiddleText('DONNEES_SOUSCRIPTION_COUVERTURE_ACTUELLE_TITLE')}</PageTitle>
          <ControlledRadioButton
            label={getMiddleText('CURRENT_COVERAGE_LABEL')}
            booleanValue
            id="activePolicy.contractOnGoing"
            name="activePolicy.contractOnGoing"
            rules={isRequiredBoolean()}
            options={booleanOptions}
            form={form}
            data-testid="CURRENT_COVERAGE"
            disabled={extraCurrentCoverageDisabled}
            containerClassName="flex flex-col gap-3"
          />

          <InfoMessage iconName={'lightbulb'} className={'w-full'}>
            {getMiddleText('ACTIVE_POLICIES_SIDE_NOTE')}
          </InfoMessage>

          {contractOnGoing && (
            <>
              {inChargeOptions && displayInChargeResp && (
                <ControlledSelect
                  form={form}
                  containerClassName="w-1/2"
                  name={`${basePath}.cancellationApplicant`}
                  label={getMiddleText('IN_CHARGE_LABEL')}
                  options={inChargeOptions}
                  rules={isRequiredSelect(inChargeOptions)}
                  data-testid="IN_CHARGE"
                />
              )}

              {selectedInChargeData.needsCancellation && (
                <>
                  <InfoMessage iconName={'lightbulb'}>
                    {getMiddleText('RESILIATION_TEXT')}
                  </InfoMessage>

                  <ControlledDatePicker
                    name={`${basePath}.requestedCancellationDate`}
                    label={getMiddleText('TERMINATION_DATE_LABEL')}
                    form={form}
                    minDate={minDate}
                    maxDate={maxDate}
                    data-testid="TERMINATION_DATE"
                    disabled={extraCurrentCoverageDisabled}
                    rules={jrmc ? isRequired : { required: false }}
                  />

                  {jrmc && isNotNilOrEmpty(jrmcCompanies) ? <ControlledSelect
                    form={form}
                    containerClassName="w-1/2"
                    name={`${basePath}.name`}
                    label={getMiddleText('INSURER_LABEL')}
                    options={jrmcCompanies}
                    rules={isRequiredSelect(jrmcCompanies)}
                    data-testid="INSURER"
                  />
                    : <ControlledInput
                      name={`${basePath}.name`}
                      label={getMiddleText('INSURER_LABEL')}
                      form={form}
                      rules={fieldsAreRequired || jrmc ? isRequired : { required: false }}
                      data-testid="INSURER"
                    />}

                  <ControlledInput
                    name={`${basePath}.contractNumber`}
                    label={getMiddleText('OLD_CONTRACT_NUMBER_LABEL')}
                    form={form}
                    rules={{
                      ...(jrmc ? isRequired : { required: false }),
                      validate: (value) =>
                        isNilOrEmpty(value) ||
                        isValidContractNumber(value) ||
                        'Le format saisi est invalide.'
                    }}
                    data-testid="OLD_CONTRACT_NUMBER"
                  />

                  {!jrmc && <ControlledInput
                    name={`${basePath}.emails.0.value`}
                    label={getMiddleText('INSURER_EMAIL_LABEL')}
                    form={form}
                    rules={{
                      validate: (value) =>
                        isNilOrEmpty(value) ||
                        isEmailValid(value) ||
                        'Le format de l\'email saisi est invalide.'
                    }}
                    data-testid="INSURER_EMAIL"
                  />}

                  {!jrmc && <ControlledInput
                    name={`${basePath}.addresses.0.fields`}
                    label={getMiddleText('INSURER_ADDRESS_LABEL')}
                    form={form}
                    rules={fieldsAreRequired ? isRequired : { required: false }}
                    data-testid="INSURER_ADDRESS"
                  />}

                  {!jrmc && <ControlledInput
                    name={`${basePath}.addresses.0.zipCode`}
                    label={getMiddleText('INSURER_ZIPCODE_LABEL')}
                    form={form}
                    formatValue={formatZipCode}
                    rules={zipCodeRules(fieldsAreRequired)}
                    data-testid="INSURER_ZIPCODE_LABEL"
                  />}

                  {!jrmc && <ControlledInput
                    name={`${basePath}.addresses.0.city`}
                    label={getMiddleText('INSURER_CITY_LABEL')}
                    form={form}
                    rules={fieldsAreRequired ? isRequired : { required: false }}
                    data-testid="INSURER_CITY"
                  />}
                </>
              )}
            </>
          )}
        </PageLayout>

        <ButtonContainer
          $onPreviousButtonClick={onPrevious}
          getMiddleText={getMiddleText}
          $onNextButtonClick={() => extraHandleSaveButtonRia(trackEvent, contractOnGoing, getAnalyticsInformation)}
          nextButtonLabel={getMiddleText('GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL')}
          displayResumeButton
          nextButtonIsDisabled={not(isValid)}
          isFormError={not(isValid)}
        />
      </form>
    </FormProvider>
  );
};

export default SouscriptionActivePolicyForm;
